import React from "react";
import { formatMarkdownToElements } from "@/utils/markdown";
import { SmallCopyButton } from "../../shared/CopyButton";
import { PromptVersion } from "@/hooks/usePrompt";
import { useLanguage } from "@/contexts";

interface PromptTextDisplayProps {
  promptText: string;
  onCopy: (text: string, type: string) => void;
  latestVersion?: PromptVersion | null;
  isVisible?: boolean;
  hideHeader?: boolean;
}

// Simple prompt header with version info
const PromptHeader: React.FC<{
  latestVersion?: PromptVersion | null;
  onCopy: () => void;
}> = ({ latestVersion, onCopy }) => {
  const { t } = useLanguage();
  return (
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300">
        {t("prompt")}{" "}
        {latestVersion && (
          <span className="text-xs text-blue-600 dark:text-blue-400 font-normal">
            (v{latestVersion.version_number} - Latest)
          </span>
        )}
      </h3>
      <SmallCopyButton onCopy={onCopy} label="Copy" />
    </div>
  );
};

// Simple prompt content container
const PromptContent: React.FC<{ text: string }> = ({ text }) => {
  const nodes = formatMarkdownToElements(text);
  return (
    <div className="bg-gray-900 dark:bg-slate-950 border border-gray-800 dark:border-slate-800 rounded-lg p-3 sm:p-4 overflow-auto max-h-[400px] sm:max-h-[500px] md:max-h-[650px] text-start text-xs sm:text-sm">
      {nodes.map((node, index) => (
        <React.Fragment key={index}>{node}</React.Fragment>
      ))}
    </div>
  );
};

// Main prompt text display component
const PromptTextDisplay: React.FC<PromptTextDisplayProps> = ({
  promptText,
  onCopy,
  latestVersion,
  isVisible = true,
  hideHeader = false,
}) => {
  if (!isVisible || !promptText) return null;
  // Produce a "pretty" copy version matching on-screen formatting: normalize newlines & decode escaped \n sequences
  const normalizeForCopy = (text: string) => {
    let out = text.replace(/\r\n?/g, '\n');
    if (out.includes('\\n')) out = out.replace(/\\n/g, '\n');
    return out;
  };

  const handleCopy = () => onCopy(normalizeForCopy(promptText), "Prompt");

  return (
    <div>
      {!hideHeader && (
        <PromptHeader latestVersion={latestVersion} onCopy={handleCopy} />
      )}
      <PromptContent text={promptText} />
    </div>
  );
};

export default PromptTextDisplay;

// Export individual components for reuse
export { PromptHeader, PromptContent };
